import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Pencil, Trash2 } from "lucide-react";

export default function StaffPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "USER" });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Staff member added");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "USER" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: editing.name, email: editing.email, role: editing.role };
      if (editing.new_password) payload.password = editing.new_password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Staff updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?\nIf they have linked records, the account will be deactivated instead.`)) return;
    try {
      const { data } = await api.delete(`/users/${u.id}`);
      toast.success(data.mode === "soft" ? "Account deactivated (had linked records)" : "Staff deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div data-testid="staff-page">
      <PageHeader title="Staff" subtitle="Tenant users"
        actions={<button onClick={() => { setOpen(true); setEditing(null); }} className="btn-primary" data-testid="new-staff-btn">New staff</button>} />

      {open && (
        <Section title="Add staff member" testId="new-staff-form" actions={<button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitCreate} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Name</label><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
            <div><label className="label block mb-1">Email</label><input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" /></div>
            <div><label className="label block mb-1">Password</label><input required minLength={8} type="text" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" /></div>
            <div><label className="label block mb-1">Role</label><select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role"><option value="USER">User (Staff)</option><option value="COMPANY_ADMIN">Company Admin</option></select></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="staff-submit-btn">Save</button></div>
          </form>
        </Section>
      )}

      {editing && (
        <Section title={`Edit · ${editing.name}`} testId="edit-staff-form" actions={<button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitEdit} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Name</label><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-staff-name" /></div>
            <div><label className="label block mb-1">Email</label><input required type="email" className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="edit-staff-email" /></div>
            <div><label className="label block mb-1">Role</label>
              <select className={inputCls} value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} data-testid="edit-staff-role">
                <option value="USER">User (Staff)</option>
                <option value="COMPANY_ADMIN">Company Admin</option>
                {user?.role === "GLOBAL_ADMIN" && <option value="GLOBAL_ADMIN">Global Admin</option>}
                {user?.role === "GLOBAL_ADMIN" && <option value="CUSTOMER">Customer</option>}
              </select>
            </div>
            <div><label className="label block mb-1">Reset Password (optional)</label><input minLength={8} type="text" className={inputCls} placeholder="Leave empty to keep current" value={editing.new_password || ""} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })} data-testid="edit-staff-password" /></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="edit-staff-submit-btn">Save changes</button></div>
          </form>
        </Section>
      )}

      <Section title={`Members (${items.length})`} testId="staff-list">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No staff yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left">
              <th className="label py-2">Name</th><th className="label py-2">Email</th><th className="label py-2">Role</th>
              <th className="label py-2">Status</th><th className="label py-2">Created</th><th className="label py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="py-2.5 font-medium">{u.name}</td>
                  <td className="py-2.5">{u.email}</td>
                  <td className="py-2.5"><Pill variant={u.role === "COMPANY_ADMIN" ? "success" : u.role === "GLOBAL_ADMIN" ? "info" : "default"}>{u.role}</Pill></td>
                  <td className="py-2.5"><Pill variant={u.status === "INACTIVE" ? "warning" : "success"}>{u.status || "ACTIVE"}</Pill></td>
                  <td className="py-2.5">{fmtDate(u.created_at)}</td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => { setEditing({ ...u, new_password: "" }); setOpen(false); }} data-testid={`edit-staff-${u.id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      {u.id !== user?.id && (
                        <button className="btn-ghost px-2 py-1 text-xs text-secondary" onClick={() => remove(u)} data-testid={`delete-staff-${u.id}`}>
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
